import { useState } from "react";
import { Plus, X, Trash2, Search, Inbox, UserPlus, Send } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const PROGRESS = [
  { key: "not-started", label: "Not Started", color: "#6B7280" },
  { key: "in-progress", label: "In Progress", color: "#2F5F80" },
  { key: "complete", label: "Complete", color: "#15803D" },
];

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

export function CustomersTab({ customers, onRefresh }) {
  const [search, setSearch] = useState("");
  const [progressFilter, setProgressFilter] = useState("all");
  const [modal, setModal] = useState(null); // {initial} | null
  const [tipsState, setTipsState] = useState({ busy: false, msg: "" });

  const sendSeasonalTips = async () => {
    if (!window.confirm("Email the current season's property-care checklist to ALL customers on file?")) return;
    setTipsState({ busy: true, msg: "" });
    try {
      const r = await api.post("/customers/seasonal-tips");
      setTipsState({ busy: false, msg: `${r.data.season} tips sent to ${r.data.sent} of ${r.data.total} customers.` });
    } catch (e) {
      setTipsState({ busy: false, msg: formatApiError(e) });
    }
  };

  const setProgress = async (id, progress) => {
    try { await api.patch(`/customers/${id}`, { progress }); onRefresh(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const visible = customers.filter((c) => {
    const q = search.toLowerCase();
    const matches = !q || [c.name, c.email, c.phone, c.location, c.service].some((v) => (v || "").toLowerCase().includes(q));
    return matches && (progressFilter === "all" || c.progress === progressFilter);
  });

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8" data-testid="customers-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Customers</div>
          <p className="mt-1 text-sm text-[#23282B]/60">Everyone who has requested a quote, plus anyone you add by hand. Track each job's progress.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={sendSeasonalTips} disabled={tipsState.busy} data-testid="customers-seasonal-tips" className="inline-flex items-center gap-2 border-2 border-[#2F5F80] text-[#2F5F80] hover:bg-[#2F5F80] hover:text-white text-xs font-bold tracking-[0.15em] px-4 py-2.5 uppercase transition-colors disabled:opacity-50">
            <Send size={14} /> {tipsState.busy ? "Sending…" : "Email Seasonal Tips"}
          </button>
          <button onClick={() => setModal({ initial: null })} data-testid="customer-add" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
            <UserPlus size={15} /> Add Customer
          </button>
        </div>
      </div>
      {tipsState.msg && <div data-testid="seasonal-tips-msg" className="mt-3 bg-[#2F5F80]/10 border-l-4 border-[#2F5F80] px-4 py-2.5 text-sm text-[#23282B]">{tipsState.msg}</div>}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#23282B]/40" />
          <input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, city…" className="pl-9 pr-3 py-2 text-sm border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none w-64" />
        </div>
        <button onClick={() => setProgressFilter("all")} data-testid="customer-filter-all" className={`px-3 py-2 text-xs font-bold tracking-[0.12em] uppercase border transition-colors ${progressFilter === "all" ? "bg-[#23282B] text-white border-[#23282B]" : "border-[#23282B]/25 text-[#23282B]"}`}>All</button>
        {PROGRESS.map((p) => (
          <button key={p.key} onClick={() => setProgressFilter(p.key)} data-testid={`customer-filter-${p.key}`} className={`px-3 py-2 text-xs font-bold tracking-[0.12em] uppercase border transition-colors ${progressFilter === p.key ? "text-white border-transparent" : "border-[#23282B]/25 text-[#23282B]"}`} style={progressFilter === p.key ? { background: p.color } : undefined}>
            {p.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-[#23282B]/20 p-10 text-center text-[#23282B]/60" data-testid="customers-empty">
          <Inbox size={28} className="mx-auto text-[#2F5F80]" />
          <div className="mt-2 text-sm">No customers match — booking requests import here automatically.</div>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="customers-list">
          {visible.map((c) => (
            <article key={c.id} className="border-2 border-[#23282B]/10 hover:border-[#23282B]/25 p-4 transition-colors" data-testid={`customer-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => setModal({ initial: c })} data-testid={`customer-edit-${c.id}`} className="font-display text-xl uppercase text-[#23282B] hover:text-[#2F5F80] transition-colors">{c.name}</button>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${c.source === "booking" ? "bg-[#9CC4DB]/20 text-[#2F5F80]" : "bg-[#23282B]/10 text-[#23282B]/70"}`}>{c.source === "booking" ? "From Booking" : "Manual"}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#23282B]/70 flex flex-wrap gap-x-4 gap-y-0.5">
                    {c.email && <span>{c.email}</span>}
                    {c.phone && <span>{c.phone}</span>}
                    {c.location && <span>{c.location}</span>}
                    {c.service && <span className="text-[#2F5F80] font-semibold">{c.service}</span>}
                  </div>
                  {c.notes && <p className="mt-1.5 text-xs text-[#23282B]/60 line-clamp-2 max-w-2xl">{c.notes}</p>}
                </div>

                {/* Progress checkboxes */}
                <div className="flex items-center gap-3 shrink-0" data-testid={`customer-progress-${c.id}`}>
                  {PROGRESS.map((p) => (
                    <label key={p.key} className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none" style={{ color: c.progress === p.key ? p.color : "#23282B66" }}>
                      <input
                        type="checkbox"
                        data-testid={`customer-${c.id}-progress-${p.key}`}
                        checked={c.progress === p.key}
                        onChange={() => setProgress(c.id, p.key)}
                        className="w-4 h-4 cursor-pointer"
                        style={{ accentColor: p.color }}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {modal && <CustomerForm initial={modal.initial} onClose={() => setModal(null)} onSaved={onRefresh} />}
    </section>
  );
}

function CustomerForm({ initial, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState({
    name: initial?.name || "", email: initial?.email || "", phone: initial?.phone || "",
    location: initial?.location || "", service: initial?.service || "", notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editing) await api.patch(`/customers/${initial.id}`, form);
      else await api.post("/customers", form);
      await onSaved();
      onClose();
    } catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this customer record?")) return;
    setBusy(true);
    try { await api.delete(`/customers/${initial.id}`); await onSaved(); onClose(); }
    catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="customer-form-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg p-7 border-t-4 border-[#9CC4DB] my-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">{editing ? "Edit Customer" : "Add Customer"}</div>
          <button type="button" onClick={onClose} data-testid="customer-form-close" className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Name *</span>
            <input data-testid="customer-name" required value={form.name} onChange={set("name")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Email</span>
            <input data-testid="customer-email" value={form.email} onChange={set("email")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Phone</span>
            <input data-testid="customer-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Location / City</span>
            <input data-testid="customer-location" value={form.location} onChange={set("location")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Service</span>
            <input data-testid="customer-service" value={form.service} onChange={set("service")} className={inputCls} /></label>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Notes</span>
            <textarea data-testid="customer-notes" rows={3} value={form.notes} onChange={set("notes")} className={inputCls} /></label>
        </div>
        {error && <div data-testid="customer-form-error" className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="mt-6 flex items-center gap-3">
          <button type="submit" disabled={busy} data-testid="customer-form-submit" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-50">
            {busy ? "Saving…" : editing ? "Save Changes" : "Add Customer"}
          </button>
          {editing && (
            <button type="button" onClick={remove} disabled={busy} data-testid="customer-delete" className="ml-auto inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-xs font-bold tracking-[0.12em] uppercase">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
